import { useEffect, useState } from 'react';
import { api, errorText, formatDateTime } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, ReasonBadge } from '../components/ui';

interface SerialRow {
  id: string;
  serial: string;
  status: 'im_lager' | 'ausgeliefert' | 'gesperrt' | 'verschrottet';
  article_id: string;
  sku: string;
  article_name: string;
  owner_name: string;
  location_code: string | null;
  lot_code: string | null;
  order_number: string | null;
  customer_name: string | null;
  updated_at: string;
}

interface SerialTrace {
  serial: SerialRow;
  movements: {
    id: string;
    booked_at: string;
    reason: string;
    from_code: string | null;
    to_code: string | null;
    document_ref: string | null;
    user_name: string | null;
  }[];
}

const STATUS_STYLE: Record<SerialRow['status'], 'ok' | 'info' | 'warn' | 'bad'> = {
  im_lager: 'ok',
  ausgeliefert: 'info',
  gesperrt: 'warn',
  verschrottet: 'bad',
};

const STATUS_LABEL: Record<SerialRow['status'], string> = {
  im_lager: 'im Lager',
  ausgeliefert: 'ausgeliefert',
  gesperrt: 'gesperrt',
  verschrottet: 'verschrottet',
};

export function SerialsPage() {
  const { can } = useAuth();
  const mayChange = can('admin', 'schichtleiter', 'lagerist', 'disponent');

  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState<SerialRow[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [trace, setTrace] = useState<SerialTrace | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      api<SerialRow[]>('/api/stock/serials', {
        query: { search: term.trim() || undefined, status: status || undefined },
      })
        .then(setRows)
        .catch((err) => setError(errorText(err)));
    }, term ? 220 : 0);
    return () => clearTimeout(timer);
  }, [term, status]);

  useEffect(() => {
    if (!openId) return;
    setTrace(null);
    api<SerialTrace>(`/api/stock/serials/${openId}/trace`)
      .then(setTrace)
      .catch((err) => setError(errorText(err)));
  }, [openId]);

  async function changeStatus(id: string, next: SerialRow['status']) {
    try {
      await api(`/api/stock/serials/${id}/status`, { method: 'PUT', body: { status: next } });
      setMessage(`Status auf "${STATUS_LABEL[next]}" gesetzt.`);
      setOpenId(null);
      setTerm((current) => current);
      const refreshed = await api<SerialRow[]>('/api/stock/serials', {
        query: { search: term.trim() || undefined, status: status || undefined },
      });
      setRows(refreshed);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <label className="field" style={{ minWidth: 280 }}>
          <span>Seriennummer, Artikelnummer oder Bezeichnung</span>
          <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="z. B. SN-00042" />
        </label>
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">alle</option>
            {(Object.keys(STATUS_LABEL) as SerialRow['status'][]).map((key) => (
              <option key={key} value={key}>
                {STATUS_LABEL[key]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Card padded={false}>
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            Keine Seriennummern gefunden. Sie entstehen beim Wareneingang seriennummernpflichtiger
            Artikel.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Seriennummer</th>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th>Status</th>
                  <th>Ort / Empfänger</th>
                  <th>Zuletzt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>
                      {row.serial}
                    </td>
                    <td>
                      <span className="mono">{row.sku}</span> {row.article_name}
                    </td>
                    <td className="small">{row.owner_name}</td>
                    <td>
                      <Badge kind={STATUS_STYLE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
                    </td>
                    <td className="small">
                      {row.status === 'ausgeliefert'
                        ? `${row.order_number ?? '—'}${row.customer_name ? ` · ${row.customer_name}` : ''}`
                        : (row.location_code ?? '—')}
                    </td>
                    <td className="small muted">{formatDateTime(row.updated_at)}</td>
                    <td className="num">
                      <button className="small ghost" onClick={() => setOpenId(row.id)}>
                        Weg ansehen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openId &&
        (!trace ? (
          <Loading />
        ) : (
          <Card
            title={`Seriennummer ${trace.serial.serial}`}
            actions={
              <>
                {mayChange && trace.serial.status !== 'gesperrt' && (
                  <button className="small" onClick={() => changeStatus(trace.serial.id, 'gesperrt')}>
                    sperren
                  </button>
                )}
                {mayChange && trace.serial.status === 'gesperrt' && (
                  <button className="small" onClick={() => changeStatus(trace.serial.id, 'im_lager')}>
                    freigeben
                  </button>
                )}
                <button className="small" onClick={() => setOpenId(null)}>
                  schliessen
                </button>
              </>
            }
            padded={false}
          >
            <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
              <strong>{trace.serial.article_name}</strong>
              <div className="small muted">
                <span className="mono">{trace.serial.sku}</span> · {trace.serial.owner_name}
                {trace.serial.lot_code ? ` · Charge ${trace.serial.lot_code}` : ''}
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Art</th>
                  <th>Von</th>
                  <th>Nach</th>
                  <th>Beleg</th>
                  <th>Benutzer</th>
                </tr>
              </thead>
              <tbody>
                {trace.movements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="small muted">{formatDateTime(movement.booked_at)}</td>
                    <td>
                      <ReasonBadge reason={movement.reason} />
                    </td>
                    <td className="mono small">{movement.from_code ?? '—'}</td>
                    <td className="mono small">{movement.to_code ?? '—'}</td>
                    <td className="small mono">{movement.document_ref ?? '—'}</td>
                    <td className="small muted">{movement.user_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}
    </>
  );
}
